import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Send, Mail, Instagram, Loader2, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = ({ prefillMessage }) => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const messageRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prefill message when a jersey is selected in the collection
  useEffect(() => {
    if (prefillMessage) {
      setFormData((prev) => ({ ...prev, message: prefillMessage }));
      // Highlight the message field briefly
      if (messageRef.current) {
        messageRef.current.focus({ preventScroll: true });
      }
    }
  }, [prefillMessage]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error(t("contact.fillFields"));
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      
      toast.success(t("contact.success"));
      
      // Reset form
      setFormData({
        name: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(t("contact.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      className="py-24 md:py-32 bg-[#050505] relative"
      ref={ref}
      data-testid="contact-section"
    >
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#009A49] mb-6 font-body">
              {t("contact.label")}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8 tracking-wide">
              {t("contact.heading")}
            </h2>
            <div className="emerald-divider mb-8" />
            
            <p className="text-zinc-400 font-body text-lg leading-relaxed mb-10">
              {t("contact.description")}
            </p>
            
            {/* Contact links */}
            <div className="space-y-6">
              <a 
                href="mailto:lockeroom.egypt@gmail.com"
                className="flex items-center gap-4 text-zinc-400 hover:text-[#009A49] transition-colors group"
                data-testid="contact-email-link"
              >
                <div className="w-12 h-12 rounded-full border border-white/10 group-hover:border-[#009A49]/50 flex items-center justify-center transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <span className="font-body">lockeroom.egypt@gmail.com</span>
              </a>
              
              <a 
                href="https://instagram.com/locker_room_egypt"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-zinc-400 hover:text-[#009A49] transition-colors group"
                data-testid="contact-instagram-link"
              >
                <div className="w-12 h-12 rounded-full border border-white/10 group-hover:border-[#009A49]/50 flex items-center justify-center transition-colors">
                  <Instagram className="w-5 h-5" />
                </div>
                <span className="font-body">@locker_room_egypt</span>
              </a>
              
              <a 
                href="https://wa.me/201037255878"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-zinc-400 hover:text-[#009A49] transition-colors group"
                data-testid="contact-whatsapp-link"
              >
                <div className="w-12 h-12 rounded-full border border-white/10 group-hover:border-[#009A49]/50 flex items-center justify-center transition-colors">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <span className="font-body">+20 103 725 5878</span>
              </a>
            </div>
          </motion.div>
          
          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form 
              onSubmit={handleSubmit} 
              className="contact-form space-y-6"
              data-testid="contact-form"
            >
              <div>
                <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">
                  {t("contact.nameLabel")}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t("contact.namePlaceholder")}
                  className="w-full"
                  data-testid="contact-name-input"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">
                  {t("contact.phoneLabel")}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t("contact.phonePlaceholder")}
                  className="w-full"
                  data-testid="contact-phone-input"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 font-body">
                  {t("contact.messageLabel")}
                </label>
                <textarea
                  ref={messageRef}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t("contact.messagePlaceholder")}
                  rows={5}
                  className="w-full resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-emerald w-full py-6 text-base font-semibold tracking-wider uppercase rounded-sm flex items-center justify-center gap-3"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t("contact.sending")}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t("contact.send")}
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
